import React, { useRef, useState, useEffect } from 'react';
import { RootState } from 'src/store/reducers/state';
import { useDispatch, useSelector } from 'react-redux';
import { useVideosActions } from 'src/store/videos/actions';
import { useParams } from 'react-router-dom';
import { uploadImageToS3 } from 'src/components/utilities/utils';
import ToastMessage from 'src/components/ToastContainer';
import { successMsg } from 'src/components/utilities/utils';

const VideoEditor = ({ videoId }: any) => {
  const dispatch = useDispatch();
  const videoRef = useRef<any>(null);
  const videoActions = useVideosActions(dispatch);
  const [captions, setCaptions] = useState<any[]>([]);
  const [chapters, setChapters] = useState<any[]>([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [editingIndex, setEditingIndex] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCaptions, setShowCaptions] = useState(true);
  const [showChapters, setShowChapters] = useState(false);
  const [ondemandCaptionUrl, setOndemandCaptionUrl] = useState('');
  const [chapterUrl, setChapterUrl] = useState('');
  const [title, setTitle] = useState('First video | acbo test - 4 March 2025');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingCaptions, setIsEditingCaptions] = useState(false);
  const [isEditingChapters, setIsEditingChapters] = useState(false);
  const [isAddingChapter, setIsAddingChapter] = useState(false);
  const [isAddingCaption, setIsAddingCaption] = useState(false);
  const [newChapterTime, setNewChapterTime] = useState('');
  const [newChapterName, setNewChapterName] = useState('');
  const [newCaptionStart, setNewCaptionStart] = useState('');
  const [newCaptionEnd, setNewCaptionEnd] = useState('');
  const [newCaptionText, setNewCaptionText] = useState('');
  const [editedChapterTime, setEditedChapterTime] = useState<{ [key: number]: string }>({});
  const activeCaptionRef = useRef<HTMLDivElement | null>(null);

  const { id } = useParams<string>();

  const { singleVideo } = useSelector((state: RootState) => {
    return {
      singleVideo: state.videos.singleVideo
    };
  });

  const { videoMedia, accessibility } = useSelector((state: RootState) => ({
    videoMedia: state.videos.videoMedia,
    accessibility: state.videos.accessibility
  }));

  useEffect(() => {
    if (activeCaptionRef.current) {
      activeCaptionRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [currentTime]);

  useEffect(() => {
    if (singleVideo) {
      setTitle(singleVideo.title);
    }
  }, [singleVideo]);
  useEffect(() => {
    const interval = setInterval(() => {
      if (videoRef.current) {
        setCurrentTime(videoRef.current.currentTime);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [videoRef]);

  const handleUploadToS3 = async (selectedFile: any, type: 'captions' | 'chapters') => {
    if (selectedFile) {
      uploadImageToS3(
        selectedFile,
        `v2/video/${type === 'chapters' ? 'chapters' : 'ondemandcaptions'}?videoId=${id}&${
          type === 'chapters' ? 'isVideoChapters' : 'isOnDemandCaption'
        }=true&`,
        type === 'captions' ? setOndemandCaptionUrl : setChapterUrl,
        type === 'captions' ? 'onDemandCaption' : 'chapterUrl'
      );
    }
  };

  const handleCaptionChange = (index: any, field: 'start' | 'end' | 'text', value: any) => {
    const updated = [...captions];
    if (field === 'start') updated[index].start = toSeconds(value);
    else if (field === 'end') updated[index].end = toSeconds(value);
    else updated[index].text = value;
    setCaptions(updated);
  };

  const handleChapterChange = (index: any, field: 'start' | 'text', value: any) => {
    const updated = [...chapters];
    if (field === 'start') {
      setEditedChapterTime((prev) => ({ ...prev, [index]: value }));
      updated[index].start = toSeconds(value);
    } else {
      updated[index].text = value;
    }
    setChapters(updated);
  };

  const toSeconds = (timeStr: string) => {
    const [hours, minutes, seconds] = timeStr.split(':');
    const [secs, millis] = seconds.split('.');
    return (
      parseInt(hours || '0') * 3600 +
      parseInt(minutes || '0') * 60 +
      parseInt(secs || '0') +
      parseInt(millis || '0') / 1000
    );
  };

  const toTimestamp = (time: number) => {
    const hours = String(Math.floor(time / 3600)).padStart(2, '0');
    const minutes = String(Math.floor((time % 3600) / 60)).padStart(2, '0');
    const seconds = String(Math.floor(time % 60)).padStart(2, '0');
    const milliseconds = String(Math.floor((time % 1) * 1000)).padStart(3, '0');
    return `${hours}:${minutes}:${seconds}.${milliseconds}`;
  };

  useEffect(() => {
    id && videoActions.getMediaUrlRequest(id);
    if (id) {
      videoActions.getAccessibilityRequest(id);
      videoActions.getVideoMediaRequest(id);
    }
  }, []);

  const parseVTT = async (url: string) => {
    const response = await fetch(url);
    const vttText = await response.text();
    const lines = vttText.split('\n');
    const parsed: any[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i].trim();
      if (!line || line.startsWith('WEBVTT')) {
        i++;
        continue;
      }

      const timeRangeMatch = line.match(
        /(\d{2}:\d{2}:\d{2}\.\d{3})\s-->\s(\d{2}:\d{2}:\d{2}\.\d{3})/
      );
      const singleTimeMatch = line.match(/^(\d{2}:\d{2}:\d{2}\.\d{3})$/);

      if (timeRangeMatch) {
        const start = toSeconds(timeRangeMatch[1]);
        const end = toSeconds(timeRangeMatch[2]);
        let text = '';

        i++;
        while (i < lines.length && lines[i].trim()) {
          text += lines[i] + ' ';
          i++;
        }

        parsed.push({ start, end, text: text.trim() });
      } else if (singleTimeMatch) {
        const start = toSeconds(singleTimeMatch[1]);
        i++;
        let text = '';
        while (i < lines.length && lines[i].trim()) {
          text += lines[i] + ' ';
          i++;
        }

        parsed.push({ start, text: text.trim() });
      } else {
        i++;
      }
    }

    return parsed;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (accessibility?.onDemandCaptionURL) {
        try {
          const parsedCaptions = await parseVTT(accessibility.onDemandCaptionURL);
          setCaptions(parsedCaptions);
        } catch (err) {
          console.error('Failed to fetch captions:', err);
        }
      }

      if (accessibility?.chaptersUrl) {
        try {
          const parsedChapters = await parseVTT(accessibility.chaptersUrl);
          setChapters(parsedChapters);
        } catch (err) {
          console.error('Failed to fetch chapters:', err);
        }
      }
    };

    fetchData();
  }, [accessibility?.onDemandCaptionURL, accessibility?.chaptersUrl]);

  const generateVTT = (items: any[], isChapter = false) => {
    let vttContent = `WEBVTT - ${title}\n\n`;
    if (isChapter) {
      items.forEach((item, index) => {
        const start = toTimestamp(item.start);
        const end = toTimestamp(item.start + 30); // Default end time: start + 30 seconds
        vttContent += `${index + 1}\n${start} --> ${end}\n${item.text}\n\n`;
      });
    } else {
      items.forEach((item) => {
        const start = toTimestamp(item.start);
        const end = toTimestamp(item.end);
        vttContent += `${start} --> ${end}\n${item.text}\n\n`;
      });
    }
    return vttContent;
  };

  const handleUpdate = async (type: 'captions' | 'chapters') => {
    const vtt = generateVTT(type === 'captions' ? captions : chapters, type === 'chapters');
    const timestamp = new Date().toISOString().replace(/[:.-]/g, '');
    const filename = `${type}-${timestamp}.vtt`;

    const updatedVttFile = new File([vtt], filename, {
      type: 'text/vtt',
      lastModified: new Date().getTime()
    });

    try {
      await handleUploadToS3(updatedVttFile, type);
      successMsg(`${type === 'captions' ? 'Captions' : 'Chapters'} updated successfully!`);
    } catch (error) {
      console.error('Update failed:', error);
      successMsg('Something went wrong while uploading.');
    }
  };

  const handleCaptionUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleUploadToS3(file, 'captions');
    }
  };

  const handleDownloadCaptions = () => {
    const vttContent = generateVTT(captions);
    const blob = new Blob([vttContent], { type: 'text/vtt' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'captions.vtt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSaveChapter = () => {
    const startTime = newChapterTime ? toSeconds(newChapterTime) : currentTime;
    if (newChapterName && !isNaN(startTime)) {
      setChapters([...chapters, { start: startTime, text: newChapterName }]);
      setIsAddingChapter(false);
      setNewChapterTime('');
      setNewChapterName('');
    }
  };

  const handleSaveCaption = () => {
    const startTime = newCaptionStart ? toSeconds(newCaptionStart) : currentTime;
    const endTime = newCaptionEnd ? toSeconds(newCaptionEnd) : startTime + 5; // Default end time: start + 5 seconds
    if (newCaptionText && !isNaN(startTime) && !isNaN(endTime) && startTime < endTime) {
      setCaptions([...captions, { start: startTime, end: endTime, text: newCaptionText }]);
      setIsAddingCaption(false);
      setNewCaptionStart('');
      setNewCaptionEnd('');
      setNewCaptionText('');
    }
  };

  const filteredCaptions = captions.filter((cap) =>
    cap.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (ondemandCaptionUrl && id) {
      const payloadData = {
        id: id,
        updateData: [{ op: 'replace', path: 'onDemandCaptionURL', value: ondemandCaptionUrl }]
      };
      videoActions.updateAccessibilityRequest(payloadData);
    }
  }, [ondemandCaptionUrl]);

  useEffect(() => {
    if (chapterUrl && id) {
      const payloadData = {
        id: id,
        updateData: [{ op: 'replace', path: 'webvttChaptersUrl', value: chapterUrl }]
      };
      videoActions.updateAccessibilityRequest(payloadData);
    }
  }, [chapterUrl, id]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  useEffect(() => {
    if (id) {
      videoActions.getSingleVideosRequest(id);
    }
  }, [id]);
  return (
    <div className="flex flex-col min-h-9/10 bg-gray-100">
      <ToastMessage />
      {/* Editable Title */}
      <div className="w-full p-4 bg-white border-b border-gray-300">
        {isEditingTitle ? (
          <input
            type="text"
            value={title}
            onChange={handleTitleChange}
            onBlur={() => setIsEditingTitle(false)}
            autoFocus
            className="w-full p-2 border border-gray-300 rounded text-xl font-bold"
          />
        ) : (
          <h1
            onClick={() => setIsEditingTitle(true)}
            className="text-xl font-bold cursor-pointer hover:bg-gray-100 p-2 rounded">
            {title}
          </h1>
        )}
      </div>

      {/* Main Content */}
      <div className="flex flex-col md:flex-row flex-1">
        {/* Video */}
        <div className="w-full md:w-2/3 bg-black flex items-center justify-center">
          <video
            className="w-full h-auto"
            controls
            ref={videoRef}
            src={videoMedia?.primaryUrl?.mP4URL}
            onPause={() => setCurrentTime(videoRef.current?.currentTime)}
            onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime)}
          />
        </div>

        {/* Right Panel */}
        <div className="w-full md:w-1/3 bg-white p-4 border-l border-gray-300 flex flex-col overflow-hidden">
          {/* <input
            type="text"
            placeholder="Search transcript"
            className="w-full mb-4 p-2 border border-gray-300 rounded"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          /> */}

          {/* Tabs for Captions and Chapters */}
          <div className="flex mb-4">
            <button
              className={`flex-1 text-center py-2 font-semibold rounded-l ${
                showCaptions ? 'text-purple-600 bg-purple-100' : 'text-gray-700 bg-gray-100'
              }`}
              onClick={() => {
                setShowCaptions(true);
                setShowChapters(false);
                setIsEditingCaptions(false);
                setIsEditingChapters(false);
                setIsAddingCaption(false);
              }}>
              Captions
            </button>
            <button
              className={`flex-1 text-center py-2 font-semibold rounded-r ${
                showChapters ? 'text-purple-600 bg-purple-100' : 'text-gray-700 bg-gray-100'
              }`}
              onClick={() => {
                setShowChapters(true);
                setShowCaptions(false);
                setIsEditingCaptions(false);
                setIsEditingChapters(false);
                setIsAddingChapter(false);
              }}>
              Chapters
            </button>
          </div>

          <div className="flex-1 overflow-y-auto pr-2 space-y-4">
            {/* Captions Section */}
            {showCaptions && (
              <div>
                {isEditingCaptions ? (
                  <>
                    <button
                      className="mb-4 text-purple-600 hover:underline"
                      onClick={() => {
                        setIsEditingCaptions(false);
                        setIsAddingCaption(false);
                      }}>
                      ← Back to Settings
                    </button>
                    <div className="space-y-2 text-sm overflow-y-auto pr-1 custom-max-h">
                      {filteredCaptions.map((caption, index) => {
                        const isActive = currentTime >= caption.start && currentTime <= caption.end;
                        return (
                          <div
                            key={index}
                            ref={isActive ? activeCaptionRef : null}
                            className={`flex items-start p-2 rounded group cursor-pointer ${
                              isActive
                                ? 'bg-yellow-100 border-l-4 border-yellow-500'
                                : 'hover:bg-gray-50'
                            }`}
                            onClick={() => setEditingIndex(index)}>
                            <div className="flex flex-col text-gray-400 text-xs w-32 mr-2">
                              <input
                                type="text"
                                value={toTimestamp(caption.start)}
                                onChange={(e) =>
                                  handleCaptionChange(index, 'start', e.target.value)
                                }
                                className="w-full p-1 border border-gray-300 rounded text-sm"
                              />
                              <span className="text-center">→</span>
                              <input
                                type="text"
                                value={toTimestamp(caption.end)}
                                onChange={(e) => handleCaptionChange(index, 'end', e.target.value)}
                                className="w-full p-1 border border-gray-300 rounded text-sm"
                              />
                            </div>
                            <div className="flex-1">
                              {editingIndex === index ? (
                                <input
                                  className="w-full border border-purple-400 p-1 rounded text-sm"
                                  value={caption.text}
                                  onChange={(e) =>
                                    handleCaptionChange(index, 'text', e.target.value)
                                  }
                                  onBlur={() => setEditingIndex(null)}
                                  autoFocus
                                />
                              ) : (
                                <p className="text-gray-800 leading-snug">{caption.text}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {isAddingCaption && (
                        <div className="flex items-start p-2 border-t border-purple-300">
                          <div className="flex flex-col text-gray-400 text-xs w-32 mr-2">
                            <input
                              type="text"
                              placeholder="HH:MM:SS.mmm"
                              value={newCaptionStart}
                              onChange={(e) => setNewCaptionStart(e.target.value)}
                              className="w-full p-1 border border-gray-300 rounded text-sm"
                            />
                            <span className="text-center">→</span>
                            <input
                              type="text"
                              placeholder="HH:MM:SS.mmm"
                              value={newCaptionEnd}
                              onChange={(e) => setNewCaptionEnd(e.target.value)}
                              className="w-full p-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <input
                            type="text"
                            placeholder="Caption Text"
                            value={newCaptionText}
                            onChange={(e) => setNewCaptionText(e.target.value)}
                            className="flex-1 p-1 border border-gray-300 rounded text-gray-800 text-sm"
                          />
                        </div>
                      )}
                    </div>
                    <div className="mt-2 flex flex-col space-y-2">
                      {isAddingCaption ? (
                        <div className="flex justify-end space-x-2">
                          <button
                            className="text-purple-600 hover:underline"
                            onClick={() => {
                              setIsAddingCaption(false);
                              setNewCaptionStart('');
                              setNewCaptionEnd('');
                              setNewCaptionText('');
                            }}>
                            Cancel
                          </button>
                          <button
                            className="bg-purple-600 text-white px-4 py-1 rounded hover:bg-purple-700"
                            onClick={handleSaveCaption}>
                            Save
                          </button>
                        </div>
                      ) : (
                        <button
                          className="w-full text-purple-600 border border-purple-600 px-4 py-1 rounded hover:bg-purple-50"
                          onClick={() => {
                            setIsAddingCaption(true);
                            setNewCaptionStart(toTimestamp(currentTime));
                            setNewCaptionEnd(toTimestamp(currentTime + 5));
                          }}>
                          Add Captions
                        </button>
                      )}
                      <button
                        className="w-full text-purple-600 border border-purple-600 px-4 py-1 rounded hover:bg-purple-50"
                        onClick={() => {
                          handleUpdate('captions');
                          setIsAddingCaption(false);
                        }}>
                        Update Captions
                      </button>
                    </div>
                  </>
                ) : captions.length === 0 ? (
                  <div className="text-center">
                    <p className="text-gray-600 mb-4">No captions available.</p>
                    <button
                      className="text-purple-600 border border-purple-600 px-4 py-2 rounded hover:bg-purple-50"
                      onClick={() => {
                        setIsEditingCaptions(true);
                        setIsAddingCaption(true);
                        setNewCaptionStart(toTimestamp(currentTime));
                        setNewCaptionEnd(toTimestamp(currentTime + 5));
                      }}>
                      Add Captions
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      className="w-full text-purple-600 border border-purple-600 px-4 py-2 rounded hover:bg-purple-50"
                      onClick={() => setIsEditingCaptions(true)}>
                      Edit Captions
                    </button>
                    <label className="w-full text-purple-600 border border-purple-600 px-4 py-2 rounded hover:bg-purple-50 inline-block text-center cursor-pointer">
                      Replace Captions
                      <input
                        type="file"
                        accept=".vtt"
                        onChange={handleCaptionUpload}
                        className="hidden"
                      />
                    </label>
                    <button
                      className="w-full text-purple-600 border border-purple-600 px-4 py-2 rounded hover:bg-purple-50"
                      onClick={handleDownloadCaptions}>
                      Download Captions
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Chapters Section */}
            {showChapters && (
              <div className="border border-purple-300 rounded p-2">
                <h3 className="text-purple-600 font-semibold mb-2">Chapters</h3>
                {isEditingChapters ? (
                  <>
                    <button
                      className="mb-4 text-purple-600 hover:underline"
                      onClick={() => {
                        setIsEditingChapters(false);
                        setIsAddingChapter(false);
                      }}>
                      ← Back to Settings
                    </button>
                    <div className="space-y-2 text-sm overflow-y-auto pr-1 custom-max-h">
                      {chapters.map((chapter, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-1 hover:bg-gray-50 rounded">
                          <input
                            type="text"
                            value={editedChapterTime[index] || toTimestamp(chapter.start)}
                            onChange={(e) => handleChapterChange(index, 'start', e.target.value)}
                            className="w-32 p-1 border border-gray-300 rounded text-gray-400 text-sm"
                          />
                          <input
                            type="text"
                            value={chapter.text}
                            onChange={(e) => handleChapterChange(index, 'text', e.target.value)}
                            className="flex-1 ml-2 p-1 border border-gray-300 rounded text-gray-800"
                          />
                        </div>
                      ))}
                      {isAddingChapter && (
                        <div className="flex items-center justify-between p-1 border-t border-purple-300">
                          <input
                            type="text"
                            placeholder="HH:MM:SS.mmm"
                            value={newChapterTime}
                            onChange={(e) => setNewChapterTime(e.target.value)}
                            className="w-32 p-1 border border-gray-300 rounded text-gray-400"
                          />
                          <input
                            type="text"
                            placeholder="Chapter Name"
                            value={newChapterName}
                            onChange={(e) => setNewChapterName(e.target.value)}
                            className="flex-1 ml-2 p-1 border border-gray-300 rounded text-gray-800"
                          />
                        </div>
                      )}
                    </div>
                    <div className="mt-2 flex flex-col space-y-2">
                      {isAddingChapter ? (
                        <div className="flex justify-end space-x-2">
                          <button
                            className="text-purple-600 hover:underline"
                            onClick={() => {
                              setIsAddingChapter(false);
                              setNewChapterTime('');
                              setNewChapterName('');
                            }}>
                            Cancel
                          </button>
                          <button
                            className="bg-purple-600 text-white px-4 py-1 rounded hover:bg-purple-700"
                            onClick={handleSaveChapter}>
                            Save
                          </button>
                        </div>
                      ) : (
                        <button
                          className="w-full text-purple-600 border border-purple-600 px-4 py-1 rounded hover:bg-purple-50"
                          onClick={() => {
                            setIsAddingChapter(true);
                            setNewChapterTime(toTimestamp(currentTime));
                          }}>
                          Add Chapter
                        </button>
                      )}
                      <button
                        className="w-full text-purple-600 border border-purple-600 px-4 py-1 rounded hover:bg-purple-50"
                        onClick={() => {
                          handleUpdate('chapters');
                          setIsAddingChapter(false);
                        }}>
                        Update Chapters
                      </button>
                    </div>
                  </>
                ) : chapters.length === 0 ? (
                  <div className="text-center">
                    <p className="text-gray-600 mb-4">No chapters available.</p>
                    <button
                      className="text-purple-600 border border-purple-600 px-4 py-2 rounded hover:bg-purple-50"
                      onClick={() => {
                        setIsEditingChapters(true);
                        setIsAddingChapter(true);
                        setNewChapterTime(toTimestamp(currentTime));
                      }}>
                      Add Chapter
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      className="w-full text-purple-600 border border-purple-600 px-4 py-2 rounded hover:bg-purple-50"
                      onClick={() => setIsEditingChapters(true)}>
                      Edit Chapters
                    </button>
                    <label className="w-full text-purple-600 border border-purple-600 px-4 py-2 rounded hover:bg-purple-50 inline-block text-center cursor-pointer">
                      Replace Chapters
                      <input
                        type="file"
                        accept=".vtt"
                        onChange={(e) => handleUploadToS3(e.target.files?.[0], 'chapters')}
                        className="hidden"
                      />
                    </label>
                    <button
                      className="w-full text-purple-600 border border-purple-600 px-4 py-2 rounded hover:bg-purple-50"
                      onClick={() => {
                        const vttContent = generateVTT(chapters, true);
                        const blob = new Blob([vttContent], { type: 'text/vtt' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'chapters.vtt';
                        a.click();
                        URL.revokeObjectURL(url);
                      }}>
                      Download Chapters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoEditor;
